import type { Currency, Language } from '@festina-life/core'
import type { ReactNode } from 'react'

import {
  DEFAULT_PERCENT_DECIMAL_SCALE,
  parsePercentage,
} from '../percentageInput/percentageInput.utils'

export type SortFn<T> = (left: T, right: T) => number

const createFormattedNumberParser = ({
  locale,
  currency,
}: {
  locale: string
  currency: string
}) => {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).formatToParts(-1000.1)
  const symbols = {
    group: parts.find((part) => part.type === 'group')?.value ?? '',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    currency: parts.find((part) => part.type === 'currency')?.value ?? '',
    literal: parts.find((part) => part.type === 'literal')?.value ?? '',
    minusSign: parts.find((part) => part.type === 'minusSign')?.value ?? '-',
  }

  return (value: unknown): number =>
    typeof value === 'string'
      ? Number(
          value
            .replaceAll(symbols.currency, '')
            .replaceAll(symbols.group, '')
            .replaceAll(symbols.literal, '')
            .replaceAll(symbols.decimal, '.')
            .replaceAll(symbols.minusSign, '-'),
        )
      : NaN
}

export const comparePrimitiveValues = <T extends unknown>({
  left,
  right,
  locale,
  currency,
}: {
  left: T
  right: T
  locale: string
  currency: Currency
}): number => createPrimitiveComparator({ locale, currency })(left, right)

export const createPrimitiveComparator = ({
  locale,
  currency,
}: {
  locale: string
  currency: Currency
}): SortFn<unknown> => {
  const parseFormattedNumber = createFormattedNumberParser({ locale, currency })
  const collator = new Intl.Collator(locale, {
    numeric: true,
    sensitivity: 'base',
  })

  return (left, right) => {
    if (left === right) {
      return 0
    }

    if (left === undefined || left === null) {
      return 1
    }

    if (right === undefined || right === null) {
      return -1
    }

    if (typeof left === 'number' && typeof right === 'number') {
      return left - right
    }

    const potentialLeftNumber = parseFormattedNumber(left)
    const potentialRightNumber = parseFormattedNumber(right)
    if (isFinite(potentialLeftNumber) && isFinite(potentialRightNumber)) {
      return potentialLeftNumber - potentialRightNumber
    }

    const potentialLeftPercentage = parsePercentage({
      value: left,
      locale,
      decimals: DEFAULT_PERCENT_DECIMAL_SCALE,
    })
    const potentialRightPercentage = parsePercentage({
      value: right,
      locale,
      decimals: DEFAULT_PERCENT_DECIMAL_SCALE,
    })
    if (
      Number.isFinite(potentialLeftPercentage) &&
      Number.isFinite(potentialRightPercentage)
    ) {
      return potentialLeftPercentage - potentialRightPercentage
    }

    if (left instanceof Date && right instanceof Date) {
      return right.getTime() - left.getTime()
    }

    if (typeof left === 'boolean' && typeof right === 'boolean') {
      return Number(right) - Number(left)
    }

    if (
      typeof left === 'string' &&
      typeof right === 'string' &&
      isStrictServerDateString(left) &&
      isStrictServerDateString(right)
    ) {
      return collator.compare(right, left)
    }

    if (typeof left === 'bigint' && typeof right === 'bigint') {
      if (left < right) {
        return -1
      } else {
        return left > right ? 1 : 0
      }
    }

    return collator.compare(`${left}`, `${right}`)
  }
}

export const isPercentage = (value: unknown, locale: Language): boolean =>
  Number.isFinite(
    parsePercentage({ value, locale, decimals: DEFAULT_PERCENT_DECIMAL_SCALE }),
  )

export const isFormattedNumber = ({
  value,
  currency,
  locale,
}: {
  value: unknown
  currency: string
  locale: string
}): number =>
  typeof value === 'string'
    ? createFormattedNumberParser({ locale, currency })(value)
    : NaN

// Matches values starting with the server date format (YYYY-MM-DD), optionally
// followed by e.g. a time. Runs inside sort comparators, so it avoids date
// parsing, which is too slow for that.
const SERVER_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/

export const isStrictServerDateString = (value: string): boolean =>
  SERVER_DATE_PREFIX.test(value)

export const shouldSortByRenderedValue = <T extends unknown>({
  leftValue,
  rightValue,
  leftRenderedValue,
  rightRenderedValue,
}: {
  leftValue: T
  rightValue: T
  leftRenderedValue: ReactNode
  rightRenderedValue: ReactNode
}): boolean => {
  if (
    typeof leftRenderedValue !== 'string' ||
    typeof rightRenderedValue !== 'string'
  ) {
    return false
  }

  if (
    isStrictServerDateString(leftRenderedValue) ||
    isStrictServerDateString(rightRenderedValue)
  ) {
    return false
  }

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return false
  }

  if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
    return false
  }

  if (
    typeof leftValue === 'string' &&
    typeof rightValue === 'string' &&
    (isStrictServerDateString(leftValue) ||
      isStrictServerDateString(rightValue))
  ) {
    return false
  }

  return true
}

export function getSortFn<T>({
  sort,
  locale,
  currency,
}: {
  sort: 'automatic' | false | SortFn<T>
  locale: string
  currency: Currency
}):
  | ((
      left: { value: T; textValue: string },
      right: { value: T; textValue: string },
    ) => number)
  | undefined {
  if (sort === false) {
    return undefined
  }

  if (typeof sort === 'function') {
    return (left, right) => sort(left.value, right.value)
  }

  const compare = createPrimitiveComparator({ locale, currency })

  return (left, right) =>
    left.textValue !== '' &&
    right.textValue !== '' &&
    shouldSortByRenderedValue({
      leftValue: left.value,
      rightValue: right.value,
      leftRenderedValue: left.textValue,
      rightRenderedValue: right.textValue,
    })
      ? compare(left.textValue, right.textValue)
      : compare(left.value, right.value)
}
